import { lightService } from "./LightService";
import { FacetRenderer } from "../renderable/Renderable";
import { AdditiveBlending, AmbientLight, CameraHelper, DirectionalLight, DirectionalLightHelper, HemisphereLight, HemisphereLightHelper, Matrix4, Mesh, MeshBasicMaterial, PlaneGeometry, PointLight, PointLightHelper, RectAreaLight, SpotLight, SpotLightHelper } from "three";
import { getQueryParameter, LightSource, LightSourceType, ShadowMapResolution } from "flavr-common";
import { mainScene } from "../mainScene";
import { textureLoaderService } from "../texture/TextureLoaderService";
import { ShadowMapViewer } from "three/examples/jsm/utils/ShadowMapViewer";
import { animationFrame$ } from "../../../messages/xr/AnimationFrame";
import { renderer } from "../RendererGlobals";
import { onWindowResize$ } from "../../../modules/html";
import { LightSystem } from "./LightSystem";
const DEFAULT_LIGHT_COLOR = 0xffffff;
const DEFAULT_LIGHT_INTENSITY = 1.0;
const DEFAULT_LIGHT_DISTANCE = 0.0;
const DEFAULT_LIGHT_DECAY = 2.0;
const DEFAULT_SPOTLIGHT_PENUMBRA = 1.0;
const DEFAULT_SPOTLIGHT_ANGLE = 1.0;
const DEFAULT_HEMISPHERELIGHT_GROUND_COLOR = 0xffffff;
const TEXTURE_FLARE = textureLoaderService.loadTexture("img/lensflare0.png");
const LENSFLARE_GEOMETRY = new PlaneGeometry(1, 1);
const LENSFLARE_MATERIAL = new MeshBasicMaterial({
    map: TEXTURE_FLARE,
    color: 0xffffff,
    blending: AdditiveBlending,
    transparent: true,
    depthWrite: false
});
function createLensflare(color) {
    const mat = LENSFLARE_MATERIAL.clone();
    mat.color.setHex(color);
    const mesh = new Mesh(LENSFLARE_GEOMETRY, mat);
    mesh.matrixAutoUpdate = false;
    mesh.rotateX(Math.PI);
    mesh.scale.set(4, 4, 1);
    mesh.updateMatrix();
    return mesh;
}
const ShadowMapViewers = new Map();
const DEBUG_SHADOWS = !!getQueryParameter(window.location.href, "debugShadows") || !!getQueryParameter(window.location.href, "debug");
if (DEBUG_SHADOWS) {
    animationFrame$.onPreRender$.add(() => {
        for (const data of ShadowMapViewers.values()) {
            if (data.cameraHelper) {
                data.cameraHelper.update();
            }
            if (data.lightHelper) {
                data.lightHelper.update();
            }
        }
    });
    animationFrame$.onLateUpdate$.add(() => {
        for (const [light, v] of ShadowMapViewers) {
            if (!light.castShadow)
                continue;
            if (v.shadowMapViewer === undefined)
                continue;
            v.shadowMapViewer.render(renderer);
        }
    });
    onWindowResize$.add(() => {
        resizeShadowMapViewers();
    });
}
function resizeShadowMapViewers() {
    const worldContainer = document.getElementById("world-container");
    let size;
    if (worldContainer) {
        size = worldContainer.offsetWidth * 0.06;
    }
    else {
        size = window.innerWidth * 0.06;
    }
    let index = 0;
    for (const data of ShadowMapViewers.values()) {
        if (data.shadowMapViewer === undefined)
            continue;
        data.shadowMapViewer.position.x = 10;
        data.shadowMapViewer.position.y = 150 + index * (size + 10);
        data.shadowMapViewer.size.width = size;
        data.shadowMapViewer.size.height = size;
        data.shadowMapViewer.update();
        data.shadowMapViewer.updateForWindowResize();
        index++;
    }
}
function handleDebugLight(light) {
    var _a;
    if (!light.castShadow)
        return;
    if (ShadowMapViewers.has(light)) {
        destroyDebugLight(light);
    }
    const data = {};
    if (light.castShadow) {
        data.shadowMapViewer = new ShadowMapViewer(light);
    }
    if ((light instanceof DirectionalLight ||
        light instanceof PointLight ||
        light instanceof SpotLight)
        && ((_a = light.shadow) === null || _a === void 0 ? void 0 : _a.camera) !== null) {
        data.cameraHelper = new CameraHelper(light.shadow.camera);
        data.cameraHelper.matrix = new Matrix4();
        data.cameraHelper.updateMatrix();
        data.cameraHelper.frustumCulled = false;
        light.add(data.cameraHelper);
    }
    if (light instanceof SpotLight) {
        data.lightHelper = new SpotLightHelper(light);
        data.lightHelper.matrix = new Matrix4();
        data.lightHelper.updateMatrix();
        data.lightHelper.frustumCulled = false;
        light.add(data.lightHelper);
    }
    else if (light instanceof PointLight) {
        data.lightHelper = new PointLightHelper(light);
        data.lightHelper.matrix = new Matrix4();
        data.lightHelper.updateMatrix();
        data.lightHelper.frustumCulled = false;
        light.add(data.lightHelper);
    }
    else if (light instanceof DirectionalLight) {
        data.lightHelper = new DirectionalLightHelper(light);
        data.lightHelper.matrix = new Matrix4();
        data.lightHelper.updateMatrix();
        data.lightHelper.frustumCulled = false;
        light.add(data.lightHelper);
    }
    else if (light instanceof HemisphereLight) {
        data.lightHelper = new HemisphereLightHelper(light, 5);
        data.lightHelper.matrix = new Matrix4();
        data.lightHelper.updateMatrix();
        data.lightHelper.frustumCulled = false;
        light.add(data.lightHelper);
    }
    ShadowMapViewers.set(light, data);
    resizeShadowMapViewers();
}
function destroyDebugLight(light) {
    const data = ShadowMapViewers.get(light);
    if (data === undefined)
        return;
    if (data.cameraHelper && data.cameraHelper.parent) {
        data.cameraHelper.parent.remove(data.cameraHelper);
    }
    if (data.lightHelper && data.lightHelper.parent) {
        data.lightHelper.parent.remove(data.lightHelper);
    }
    data.shadowMapViewer = undefined;
    data.lightHelper = undefined;
    data.cameraHelper = undefined;
    ShadowMapViewers.delete(light);
}
function creatLight(params) {
    switch (params.type) {
        case LightSourceType.Ambient:
            return new AmbientLight();
        case LightSourceType.Directional:
            const light = new DirectionalLight();
            light.target.matrixAutoUpdate = false;
            light.target.position.set(0, 0, -1);
            light.target.updateMatrix();
            params.targetUid === undefined && light.add(light.target);
            return light;
        case LightSourceType.Hemisphere:
            return new HemisphereLight();
        case LightSourceType.Point:
            return new PointLight();
        case LightSourceType.Spot: {
            const light = new SpotLight();
            light.target.matrixAutoUpdate = false;
            light.target.position.set(0, 0, -1);
            light.target.updateMatrix();
            params.targetUid === undefined && light.add(light.target);
            return light;
        }
        case LightSourceType.Area:
            return new RectAreaLight();
    }
}
function getValueOrDefault(value, defalut) {
    return value !== undefined ? value : defalut;
}
function getLightColor(params) {
    return getValueOrDefault(params.color, DEFAULT_LIGHT_COLOR);
}
function getLightIntensity(params) {
    return getValueOrDefault(params.intensity, DEFAULT_LIGHT_INTENSITY);
}
function getLightGroundColor(params) {
    return getValueOrDefault(params.groundColor, DEFAULT_HEMISPHERELIGHT_GROUND_COLOR);
}
function getLightDistance(params) {
    return getValueOrDefault(params.distance, DEFAULT_LIGHT_DISTANCE);
}
function getLightDecay(params) {
    return getValueOrDefault(params.decay, DEFAULT_LIGHT_DECAY);
}
function getLightPenumbra(params) {
    return getValueOrDefault(params.penumbra, DEFAULT_SPOTLIGHT_PENUMBRA);
}
function getLightAngle(params) {
    return getValueOrDefault(params.angle, DEFAULT_SPOTLIGHT_ANGLE);
}
function getShadowParams(params) {
    var _a;
    return (_a = params.lightSourceShadowParams) !== null && _a !== void 0 ? _a : params.shadow;
}
function getShadowMapResulution(shadow) {
    switch (shadow.resolution) {
        case ShadowMapResolution.LOW:
            return 1024;
        case ShadowMapResolution.MEDIUM:
            return 2048;
        case ShadowMapResolution.HIGH:
            return 4096;
        default:
            return 2048;
    }
}
function updateLightParams(light, params) {
    light.color.setHex(getLightColor(params));
    light.intensity = getLightIntensity(params);
    const shadowParams = getShadowParams(params);
    if (shadowParams !== undefined) {
        light.castShadow = true;
        if (light instanceof DirectionalLight ||
            light instanceof PointLight ||
            light instanceof SpotLight) {
            light.shadow.bias = getValueOrDefault(shadowParams.bias, -0.0001);
            light.shadow.normalBias = getValueOrDefault(shadowParams.normalBias, 0);
            light.shadow.radius = getValueOrDefault(shadowParams.radius, 1);
            light.shadow.blurSamples = getValueOrDefault(shadowParams.blurSamples, 8);
            light.shadow.mapSize.setScalar(getShadowMapResulution(shadowParams));
        }
    }
    else {
        light.castShadow = false;
    }
    switch (params.type) {
        case LightSourceType.Directional: {
            const frustumRadius = shadowParams ? shadowParams.frustumRadius : undefined;
            const d = frustumRadius !== null && frustumRadius !== void 0 ? frustumRadius : 25;
            const nearParam = shadowParams ? shadowParams.near : undefined;
            const farParam = shadowParams ? shadowParams.far : undefined;
            light.shadow.camera.left = -d;
            light.shadow.camera.right = d;
            light.shadow.camera.top = d;
            light.shadow.camera.bottom = -d;
            light.shadow.camera.near = nearParam === undefined ? 0.1 : nearParam;
            light.shadow.camera.far = farParam === undefined ? 500 : farParam;
            break;
        }
        case LightSourceType.Hemisphere:
            ;
            light.groundColor.setHex(getLightGroundColor(params));
            break;
        case LightSourceType.Point:
            ;
            light.distance = getLightDistance(params);
            light.decay = getLightDecay(params);
            const nearParam = shadowParams ? shadowParams.near : undefined;
            const farParam = shadowParams ? shadowParams.far : undefined;
            light.shadow.camera.near = getValueOrDefault(nearParam, 0.1);
            light.shadow.camera.far = getValueOrDefault(farParam, 50);
            break;
        case LightSourceType.Spot: {
            ;
            light.distance = getLightDistance(params);
            light.decay = getLightDecay(params);
            light.penumbra = getLightPenumbra(params);
            light.angle = getLightAngle(params);
            const nearParam = shadowParams ? shadowParams.near : undefined;
            const farParam = shadowParams ? shadowParams.far : undefined;
            light.shadow.camera.near = getValueOrDefault(nearParam, 0.1);
            light.shadow.camera.far = getValueOrDefault(farParam, 50);
            break;
        }
        case LightSourceType.Area: {
            if (params.width !== undefined)
                light.width = params.width;
            if (params.height !== undefined)
                light.height = params.height;
            break;
        }
    }
}
export class LightSourceRenderable extends FacetRenderer {
    constructor(owner) {
        super(owner);
        this.owner = owner;
        // active
        this.enabled = true;
        // visible in scenegraph
        this.visible = true;
        this.lightSystem = owner.renderer.getSystem(LightSystem);
        this.enabled = this.lightSystem.dynamicLightsEnabled;
    }
    destroy() {
        if (this.light) {
            this.light.parent && this.light.parent.remove(this.light);
            destroyDebugLight(this.light);
        }
        this.light = undefined;
        this.lensflare = undefined;
        this.unregisterTransformUpdate();
        if (this.animation) {
            this.owner.renderer.removeAnimationCallback(this.owner.modelUID, LightSource);
            this.animation = undefined;
        }
    }
    unregisterTransformUpdate() {
        if (this.targetUid) {
            this.owner.renderer.unregisterTransformUpdate(this.owner.modelUID, LightSource, this.targetUid);
            this.targetUid = undefined;
        }
    }
    setVisible(visible) {
        this.visible = visible;
        if (this.light) {
            if (visible) {
                if (DEBUG_SHADOWS) {
                    handleDebugLight(this.light);
                }
            }
            else {
                destroyDebugLight(this.light);
            }
        }
        if (!this.enabled)
            return;
        this.updateVisibility(visible, true);
    }
    updateVisibility(visible, removeLight) {
        if (!this.light)
            return;
        if ((!visible || !this.owner.isModelInCurrentRoom()) && (this.type !== LightSourceType.Ambient || removeLight)) {
            this.light.parent && this.light.parent.remove(this.light);
        }
        else {
            lightService.enableDefaultLight(false);
            mainScene.add(this.light);
        }
        if (this.light.castShadow && visible) {
            this.owner.renderer.updateShadowForModel(this.owner.modelUID);
        }
    }
    updateFacet(facet) {
        if (this.light && this.type === facet.params.type) {
            if (facet.params.animationDuration) {
                this.startAnimation(facet.params, facet.params.animationDuration);
            }
            else {
                updateLightParams(this.light, facet.params);
            }
        }
        else {
            if (this.light) {
                this.destroy();
            }
            this.type = facet.params.type;
            this.light = creatLight(facet.params);
            this.light.matrixAutoUpdate = false;
            this.light.frustumCulled = false;
            updateLightParams(this.light, facet.params);
            if (DEBUG_SHADOWS) {
                handleDebugLight(this.light);
            }
        }
        if (facet.params.lensflare) {
            if (!this.lensflare) {
                this.lensflare = createLensflare(facet.params.color);
                this.light.add(this.lensflare);
            }
            this.lensflare.visible = facet.params.intensity > 0;
        }
        else if (this.lensflare) {
            this.light.remove(this.lensflare);
            this.lensflare = undefined;
        }
        if (facet.params.targetUid !== this.targetUid) {
            this.unregisterTransformUpdate();
            if (facet.params.targetUid !== undefined) {
                if (this.targetUid === undefined && (this.light instanceof SpotLight || this.light instanceof DirectionalLight)) {
                    this.light.remove(this.light.target);
                }
                this.targetUid = facet.params.targetUid;
                this.owner.renderer.registerTransformUpdate(this.owner.modelUID, LightSource, this.targetUid, (model, removed) => {
                    this.onTargetUpdate(model, removed);
                });
            }
        }
        if (this.light.castShadow) {
            this.owner.renderer.updateShadowForModel(this.owner.modelUID);
        }
        if (this.type !== LightSourceType.Ambient && this.visible && !this.enabled) {
            this.updateVisibility(false, false);
        }
        this.lightSystem.changeLightsourcesOnModels();
    }
    startAnimation(params, duration) {
        if (!this.light)
            return;
        this.light.color.setHex(getLightColor(params));
        const shadowParams = getShadowParams(params);
        if (shadowParams !== undefined) {
            this.light.castShadow = true;
            if ((this.light instanceof DirectionalLight ||
                this.light instanceof PointLight ||
                this.light instanceof SpotLight)
                && this.light.shadow !== undefined) {
                this.light.shadow.bias = getValueOrDefault(shadowParams.bias, 0);
                this.light.shadow.radius = getValueOrDefault(shadowParams.radius, 1);
                this.light.shadow.mapSize.setScalar(getShadowMapResulution(shadowParams));
            }
        }
        else {
            this.light.castShadow = false;
        }
        this.animation = {
            progress: 0,
            duration
        };
        const intensity = getLightIntensity(params);
        if (intensity !== this.light.intensity) {
            this.animation.intensity = {
                begin: this.light.intensity,
                end: intensity
            };
        }
        switch (params.type) {
            case LightSourceType.Hemisphere:
                ;
                this.light.groundColor.setHex(getLightGroundColor(params));
                break;
            case LightSourceType.Point:
                {
                    const light = this.light;
                    light.distance = getLightDistance(params);
                    const decay = getLightDecay(params);
                    if (decay !== light.decay) {
                        this.animation.decay = { begin: light.decay, end: decay };
                    }
                }
                break;
            case LightSourceType.Spot:
                {
                    const light = this.light;
                    light.distance = getLightDistance(params);
                    const decay = getLightDecay(params);
                    if (decay !== light.decay) {
                        this.animation.decay = { begin: light.decay, end: decay };
                    }
                    const penumbra = getLightPenumbra(params);
                    if (penumbra !== light.penumbra) {
                        this.animation.penumbra = { begin: light.penumbra, end: penumbra };
                    }
                    const angle = getLightAngle(params);
                    if (angle !== light.angle) {
                        this.animation.angle = { begin: light.angle, end: angle };
                    }
                }
                break;
        }
        if (params.type === LightSourceType.Point) {
            ;
            this.light.distance = getLightDistance(params);
        }
        this.owner.renderer.addAnimationCallback(this.owner.modelUID, LightSource, (dt) => {
            this.updateAnimation(dt);
        });
    }
    updateAnimation(dt) {
        if (this.animation && this.light) {
            this.animation.progress += 1000 * dt;
            const f = Math.min(this.animation.progress / this.animation.duration, 1);
            const g = 1 - f;
            if (this.animation.intensity) {
                this.light.intensity = this.animation.intensity.begin * g + this.animation.intensity.end * f;
            }
            if (this.animation.decay && "decay" in this.light) {
                ;
                this.light.decay = this.animation.decay.begin * g + this.animation.decay.end * f;
            }
            if (this.animation.angle && "angle" in this.light) {
                ;
                this.light.angle = this.animation.angle.begin * g + this.animation.angle.end * f;
            }
            if (this.animation.penumbra && "penumbra" in this.light) {
                ;
                this.light.penumbra = this.animation.penumbra.begin * g + this.animation.penumbra.end * f;
            }
            if (f === 1) {
                this.animation = undefined;
            }
            if (this.light.castShadow) {
                this.owner.renderer.updateShadowForModel(this.owner.modelUID);
            }
        }
        if (!this.animation) {
            this.owner.renderer.removeAnimationCallback(this.owner.modelUID, LightSource);
        }
    }
    onTargetUpdate(model, removed) {
        if (removed) {
            this.targetUid = undefined;
            return;
        }
        if (this.light && (this.light instanceof SpotLight || this.light instanceof DirectionalLight)) {
            this.light.target.matrixWorld.copy(model.matrixWorld);
            if (this.light.castShadow) {
                this.owner.renderer.updateShadowForModel(this.owner.modelUID);
            }
        }
        else {
            this.unregisterTransformUpdate();
        }
    }
    updateTransform() {
        if (this.light) {
            this.light.matrix.copy(this.owner.matrixWorld);
            this.light.updateMatrixWorld(true);
            if (this.light.castShadow) {
                this.owner.renderer.updateShadowForModel(this.owner.modelUID);
            }
        }
    }
}
